import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Blog.css';
import SEO from '../components/SEO';

interface BlogPost {
  id: string;
  title: string;
  excerpt: string;
  date: string;
  author: string;
  category: string;
  image: string;
  readTime: string;
  slug: string;
  source: {
    name: string;
    url: string;
  };
}

const Blog: React.FC = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);

  // Categories array declaration
  const categories: string[] = ['all', 'Hormone Therapy', 'Women\'s Health', 'Thyroid Health', 'Nutrition', 'Lifestyle', 'Adrenal Health'];

  // Blog data with sources
  const blogPosts: BlogPost[] = [
    {
      id: '1',
      title: 'Understanding Bioidentical Hormone Replacement Therapy',
      excerpt: 'Learn about the benefits and considerations of bioidentical hormone replacement therapy for managing menopause symptoms and hormonal imbalances.',
      date: 'March 18, 2025',
      author: 'Dr. Sarah Johnson',
      category: 'Hormone Therapy',
      image: 'https://images.unsplash.com/photo-1631815588090-d4bfec5b1ccb?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      readTime: '8 min read',
      slug: 'understanding-bioidentical-hormone-therapy',
      source: {
        name: 'Women in Balance Institute',
        url: 'https://womeninbalance.org/resources/bioidentical-hormone-faqs/'
      }
    },
    {
      id: '2',
      title: 'The Connection Between Thyroid Health and Overall Wellness',
      excerpt: 'Discover how thyroid function affects your energy levels, weight, mood, and more. Learn about thyroid testing and optimization strategies.',
      date: 'March 10, 2025',
      author: 'Dr. Michael Chen',
      category: 'Thyroid Health',
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      readTime: '6 min read',
      slug: 'thyroid-health-and-wellness',
      source: {
        name: 'American Thyroid Association',
        url: 'https://www.thyroid.org/thyroid-information/'
      }
    },
    {
      id: '3',
      title: 'Nutrition for Hormonal Balance: Foods That Help',
      excerpt: 'Explore how specific nutrients and dietary patterns can support hormone production and metabolism for better health and symptom management.',
      date: 'February 28, 2025',
      author: 'Emma Rodriguez, RD',
      category: 'Nutrition',
      image: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      readTime: '7 min read',
      slug: 'nutrition-for-hormonal-balance',
      source: {
        name: 'Cleveland Clinic',
        url: 'https://health.clevelandclinic.org/eat-these-foods-to-reduce-stress-and-anxiety/'
      }
    },
    {
      id: '4',
      title: 'Managing Perimenopause: What Every Woman Should Know',
      excerpt: 'Navigate the transitional years before menopause with strategies for managing symptoms and maintaining quality of life.',
      date: 'February 15, 2025',
      author: 'Dr. Lisa Thompson',
      category: 'Women\'s Health',
      image: 'https://images.unsplash.com/photo-1516585427167-9f4af9627e6c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      readTime: '9 min read',
      slug: 'managing-perimenopause',
      source: {
        name: 'North American Menopause Society',
        url: 'https://www.menopause.org/for-women/menopauseflashes/menopause-symptoms-and-treatments/hormone-therapy-benefits-risks'
      }
    },
    {
      id: '5',
      title: 'Testosterone Therapy for Women: Benefits and Considerations',
      excerpt: "Understand how low testosterone affects women's health and the potential benefits and risks of testosterone supplementation.",
      date: 'February 8, 2025',
      author: 'Dr. James Wilson',
      category: 'Hormone Therapy',
      image: 'https://images.unsplash.com/photo-1530026405186-ed1f139313f8?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      readTime: '7 min read',
      slug: 'testosterone-therapy-for-women',
      source: {
        name: 'Harvard Health Publishing',
        url: 'https://www.health.harvard.edu/womens-health/testosterone-therapy-is-it-for-women'
      }
    },
    {
      id: '6',
      title: 'Sleep and Hormonal Health: The Critical Connection',
      excerpt: 'Learn how sleep quality affects hormone production and balance, and discover strategies for better sleep to support hormonal health.',
      date: 'January 26, 2025',
      author: 'Dr. Rachel Kim',
      category: 'Lifestyle',
      image: 'https://images.unsplash.com/photo-1541781774459-bb2af2f05b55?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      readTime: '6 min read',
      slug: 'sleep-and-hormonal-health',
      source: {
        name: 'Sleep Foundation',
        url: 'https://www.sleepfoundation.org/physical-health/how-do-hormones-affect-sleep'
      }
    },
    {
      id: '7',
      title: 'Stress Management: Your Adrenal Health Matters',
      excerpt: 'Understand how chronic stress affects your adrenal function and hormone balance, with practical strategies for stress reduction.',
      date: 'January 14, 2025',
      author: 'Dr. Susan Miller',
      category: 'Adrenal Health',
      image: 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      readTime: '8 min read',
      slug: 'stress-management-adrenal-health',
      source: {
        name: 'Mayo Clinic',
        url: 'https://www.mayoclinic.org/healthy-lifestyle/stress-management/in-depth/stress/art-20046037'
      }
    },
    {
      id: '8',
      title: 'Exercise for Hormonal Balance at Any Age',
      excerpt: 'Discover how different types of physical activity can help regulate hormones and improve symptoms of hormonal imbalance.',
      date: 'January 5, 2025',
      author: 'Mark Davis, CPT',
      category: 'Lifestyle',
      image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
      readTime: '7 min read',
      slug: 'exercise-for-hormonal-balance',
      source: {
        name: 'ACE Fitness',
        url: 'https://www.acefitness.org/resources/everyone/blog/6392/exercise-and-hormones-how-to-find-balance/'
      }
    },
  ];

  // Blog page schema for rich results
  const blogSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "VitalBalanceHormones Wellness Blog",
    "description": "Expert insights, research, and practical advice for women's hormonal health including bioidentical hormone therapy, thyroid health, and wellness strategies.",
    "url": "https://vitalbalancehormones.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "VitalBalanceHormones",
      "logo": {
        "@type": "ImageObject",
        "url": "https://vitalbalancehormones.com/logo512.png"
      }
    }
  };
  
  // Blog listing schema with individual articles
  const blogListingSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": blogPosts.map((post, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "BlogPosting",
        "headline": post.title,
        "description": post.excerpt,
        "author": {
          "@type": "Person",
          "name": post.author
        },
        "datePublished": post.date,
        "image": post.image,
        "publisher": {
          "@type": "Organization",
          "name": "VitalBalanceHormones",
          "logo": {
            "@type": "ImageObject",
            "url": "https://vitalbalancehormones.com/logo512.png"
          }
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://vitalbalancehormones.com/blog/${post.slug}`
        }
      }
    }))
  };

  useEffect(() => {
    // Filter posts based on category and search query
    let filtered = [...blogPosts];
    
    if (activeFilter !== 'all') {
      filtered = filtered.filter(post => post.category === activeFilter);
    }
    
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(post => 
        post.title.toLowerCase().includes(query) || 
        post.excerpt.toLowerCase().includes(query) ||
        post.category.toLowerCase().includes(query)
      );
    }
    
    setFilteredPosts(filtered);
  }, [activeFilter, searchQuery]);

  return (
    <div className="blog-page">
      <SEO 
        title="Hormone Health Blog | VitalBalanceHormones Wellness Resources"
        description="Expert articles on women's hormone health, bioidentical hormone therapy, menopause, perimenopause, thyroid health, and wellness strategies from our hormone specialists."
        keywords="hormone health blog, bioidentical hormone articles, menopause blog, perimenopause resources, women's health articles, hormone balance tips, BHRT education"
        url="/blog"
        schema={[blogSchema, blogListingSchema]}
      />
      
      {/* Hero Section */}
      <section className="blog-hero">
        <div className="blog-hero-overlay"></div>
        <div className="blog-hero-container">
          <div className="blog-hero-content">
            <h1 className="blog-hero-title">Wellness Blog</h1>
            <p className="blog-hero-subtitle">
              Insights, research, and practical advice for your hormonal health journey
            </p>
          </div>
        </div>
      </section>

      {/* Featured Video Section */}
      <section className="blog-featured-video">
        <div className="blog-container">
          <div className="video-section-content">
            <div className="video-intro">
              <h2 className="video-title">Featured Video: Understanding Hormone Health</h2>
              <p className="video-description">
                Watch our latest educational video on hormone balance and wellness strategies. 
                Learn from experts about the importance of hormonal health in your overall well-being.
              </p>
            </div>
            <div className="video-container">
              <div className="video-wrapper">
                <iframe
                  src="https://www.youtube.com/embed/q-DMKsFPOBw"
                  title="Understanding Hormone Health - VitalBalanceHormones"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="video-iframe"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Author Section */}
      <section className="blog-author-section">
        <div className="blog-container">
          <div className="author-content">
            <div className="author-image">
              <img src="../assets/team-doctor.jpg" alt="Kelly Buckley" />
            </div>
            <div className="author-text">
              <h2>Educational Content & Resources</h2>
              <p>
                Our blog features carefully curated educational content to help you better understand hormone health and wellness. 
                All articles are reviewed and approved by Kelly Buckley, PA-C, who brings years of specialized experience in 
                women's hormone health to ensure the information meets high standards for accuracy and relevance.
              </p>
              <p>
                We source content from reputable medical institutions, research organizations, and health authorities to provide 
                you with evidence-based information. This content is intended for educational purposes only and is designed to 
                help you make informed decisions about your health journey.
              </p>
              <div className="educational-disclaimer">
                <p><strong>Educational Purpose:</strong> The information provided here is for educational purposes only and does not constitute medical advice, diagnosis, or treatment recommendations. Always consult with qualified healthcare professionals for personalized medical guidance.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Content */}
      <div className="blog-content">
        <div className="blog-container">
          {/* Filter and Search Section */}
          <div className="blog-toolbar">
            <div className="blog-filter">
              <div className="blog-categories">
                {categories.map((category) => (
                  <button 
                    key={category}
                    className={`blog-category-button ${activeFilter === category ? 'active' : ''}`}
                    onClick={() => setActiveFilter(category)}
                  >
                    {category.charAt(0).toUpperCase() + category.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="blog-search">
              <div className="search-wrapper">
                <i className="icon-search"></i>
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                {searchQuery && (
                  <button 
                    className="search-clear" 
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    <i className="icon-times"></i>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Blog Posts Grid */}
          {filteredPosts.length > 0 ? (
            <div className="blog-grid">
              {filteredPosts.map(post => (
                <article key={post.id} className="blog-card">
                  <div className="blog-card-image">
                    <img src={post.image} alt={post.title} />
                    <div className="blog-card-category">{post.category}</div>
                  </div>
                  <div className="blog-card-content">
                    <div className="blog-card-meta">
                      <span className="blog-card-date">{post.date}</span>
                      <span className="blog-card-dot">•</span>
                      <span className="blog-card-read-time">{post.readTime}</span>
                    </div>
                    <h2 className="blog-card-title">{post.title}</h2>
                    <p className="blog-card-excerpt">{post.excerpt}</p>
                    <div className="blog-card-source">
                      Source: <a href={post.source.url} target="_blank" rel="noopener noreferrer">{post.source.name}</a>
                    </div>
                    <div className="blog-card-footer">
                      <a 
                        href={post.source.url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="blog-card-button"
                      >
                        Read Article
                        <i className="icon-arrow-right"></i>
                      </a>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="blog-no-results">
              <i className="icon-search no-results-icon"></i>
              <h3>No articles found</h3>
              <p>Try adjusting your search or filter to find what you're looking for.</p>
              <button className="reset-search" onClick={() => {setSearchQuery(''); setActiveFilter('all');}}>
                Reset Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* SEO Boost Section - Hormone Health Resources */}
      <section className="blog-seo-boost">
        <div className="blog-container">
          <div className="seo-boost-content">
            <div className="seo-boost-header">
              <h2>Comprehensive Hormone Health Resources</h2>
              <p className="seo-boost-intro">
                Explore our extensive collection of evidence-based information on women's hormone health, 
                bioidentical hormone therapy, and wellness strategies to support your health journey.
              </p>
            </div>

            <div className="seo-boost-grid">
              {/* Hormone Conditions */}
              <div className="seo-boost-section">
                <h3>Common Hormone Imbalances We Address</h3>
                <div className="condition-tags">
                  <span className="condition-tag">Menopause Symptoms</span>
                  <span className="condition-tag">Perimenopause Transition</span>
                  <span className="condition-tag">Estrogen Dominance</span>
                  <span className="condition-tag">Low Progesterone</span>
                  <span className="condition-tag">Testosterone Deficiency</span>
                  <span className="condition-tag">Thyroid Disorders</span>
                  <span className="condition-tag">Adrenal Fatigue</span>
                  <span className="condition-tag">PCOS Management</span>
                  <span className="condition-tag">Insulin Resistance</span>
                  <span className="condition-tag">Cortisol Imbalance</span>
                </div>
              </div>

              {/* Treatment Options */}
              <div className="seo-boost-section">
                <h3>Bioidentical Hormone Therapy Options</h3>
                <div className="treatment-list">
                  <div className="treatment-item">
                    <h4>Estrogen Replacement</h4>
                    <p>Bioidentical estradiol and estriol for menopause symptom relief</p>
                  </div>
                  <div className="treatment-item">
                    <h4>Progesterone Therapy</h4>
                    <p>Natural progesterone for cycle regulation and mood balance</p>
                  </div>
                  <div className="treatment-item">
                    <h4>Testosterone Optimization</h4>
                    <p>Low-dose testosterone for energy, libido, and muscle health</p>
                  </div>
                  <div className="treatment-item">
                    <h4>Thyroid Support</h4>
                    <p>T3, T4, and reverse T3 optimization for metabolic health</p>
                  </div>
                </div>
              </div>

              {/* Symptoms We Treat */}
              <div className="seo-boost-section">
                <h3>Symptoms We Help Resolve</h3>
                <div className="symptoms-grid">
                  <div className="symptom-category">
                    <h4>Physical Symptoms</h4>
                    <ul>
                      <li>Hot flashes and night sweats</li>
                      <li>Weight gain and difficulty losing weight</li>
                      <li>Fatigue and low energy</li>
                      <li>Sleep disturbances</li>
                      <li>Hair loss and thinning</li>
                      <li>Dry skin and aging</li>
                    </ul>
                  </div>
                  <div className="symptom-category">
                    <h4>Emotional & Mental</h4>
                    <ul>
                      <li>Mood swings and irritability</li>
                      <li>Anxiety and depression</li>
                      <li>Brain fog and memory issues</li>
                      <li>Lack of motivation</li>
                      <li>Emotional instability</li>
                      <li>Decreased confidence</li>
                    </ul>
                  </div>
                  <div className="symptom-category">
                    <h4>Sexual & Reproductive</h4>
                    <ul>
                      <li>Low libido and sexual desire</li>
                      <li>Vaginal dryness</li>
                      <li>Painful intercourse</li>
                      <li>Irregular menstrual cycles</li>
                      <li>Severe PMS symptoms</li>
                      <li>Fertility concerns</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Age-Specific Information */}
              <div className="seo-boost-section">
                <h3>Hormone Health by Life Stage</h3>
                <div className="life-stages">
                  <div className="life-stage">
                    <h4>30s - Reproductive Years</h4>
                    <p>PMS management, fertility support, and early hormone optimization</p>
                  </div>
                  <div className="life-stage">
                    <h4>40s - Perimenopause</h4>
                    <p>Managing the transition with bioidentical hormone support</p>
                  </div>
                  <div className="life-stage">
                    <h4>50s+ - Menopause & Beyond</h4>
                    <p>Comprehensive hormone replacement for long-term health</p>
                  </div>
                </div>
              </div>

              {/* Testing & Monitoring */}
              <div className="seo-boost-section">
                <h3>Advanced Hormone Testing</h3>
                <div className="testing-info">
                  <div className="test-type">
                    <h4>Comprehensive Panels</h4>
                    <p>Complete hormone profiles including estrogen, progesterone, testosterone, thyroid, and adrenal markers</p>
                  </div>
                  <div className="test-type">
                    <h4>Specialized Testing</h4>
                    <p>DUTCH testing, food sensitivity panels, and nutrient assessments for personalized treatment</p>
                  </div>
                  <div className="test-type">
                    <h4>Ongoing Monitoring</h4>
                    <p>Regular follow-up testing to ensure optimal hormone levels and treatment effectiveness</p>
                  </div>
                </div>
              </div>

              {/* Lifestyle Integration */}
              <div className="seo-boost-section">
                <h3>Integrative Wellness Approach</h3>
                <div className="wellness-pillars">
                  <div className="pillar">
                    <i className="icon-nutrition pillar-icon"></i>
                    <h4>Nutrition Guidance</h4>
                    <p>Hormone-supporting nutrition plans and supplement recommendations</p>
                  </div>
                  <div className="pillar">
                    <i className="icon-exercise pillar-icon"></i>
                    <h4>Exercise Optimization</h4>
                    <p>Fitness strategies that support hormonal balance at every age</p>
                  </div>
                  <div className="pillar">
                    <i className="icon-sleep pillar-icon"></i>
                    <h4>Sleep Enhancement</h4>
                    <p>Sleep hygiene and hormone therapy for restorative rest</p>
                  </div>
                  <div className="pillar">
                    <i className="icon-stress pillar-icon"></i>
                    <h4>Stress Management</h4>
                    <p>Techniques to reduce cortisol and support adrenal health</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;